import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown, { type ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { NewsletterSignup } from "@/components/newsletter/NewsletterSignup";
import { useSite } from "@/hooks/use-site";
import {
  getPost,
  getPostNeighbors,
  getTranslationPath,
  getTranslations,
  isScheduledPost,
} from "@/lib/blog";
import { locales, localizeUrl, type Locale } from "@/lib/site";
import { formatPostDate } from "@/utils/date";

import { parseBlockquoteDirectiveFromParagraph } from "@/lib/blog-markdown-directives";
import { BlogArticleJsonLd } from "./BlogArticleJsonLd";
import { BlogBackToTop } from "./BlogBackToTop";
import { BlogLicenseNotice } from "./BlogLicenseNotice";
import { BlogPostNavigation } from "./BlogPostNavigation";
import { RepublishKit } from "./RepublishKit";
import { MarkdownAudio } from "./MarkdownAudio";
import { MarkdownGallery } from "./MarkdownGallery";
import { MarkdownVideo } from "./MarkdownVideo";
import { MarkdownWebAppScreenshot } from "./MarkdownWebAppScreenshot";
import { ProductCTA } from "./ProductCTA";
import { BLOG_TAG_LABELS } from "./tag-labels";

const isExternalHref = (href: string) =>
  /^(https?:)?\/\//i.test(href) || /^(mailto:|tel:)/i.test(href);

// Static files live in /public and are served from the root, so they must not
// be locale-prefixed. Detect a file extension on the final path segment.
const isAssetHref = (href: string) =>
  /\.[a-z0-9]{2,5}([?#]|$)/i.test(href.split(/[?#]/)[0].split("/").pop() ?? "");

const revealOnLoad = "animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both";

export function BlogPostPage({ slug }: { slug: string }) {
  const { site, messages, href } = useSite();
  const post = getPost(site, slug);
  const blog = localizeUrl("/blog", site.locale, href);

  if (!post) {
    return (
      <SiteLayout>
        <PageBreadcrumb items={[{ label: messages.navBlog, href: blog }]} />
        <section className="mx-auto max-w-3xl px-6 py-24">
          <h1 className={`text-4xl font-semibold ${revealOnLoad}`}>{messages.notFoundTitle}</h1>
          <p
            className={`mt-4 text-muted-foreground ${revealOnLoad}`}
            style={{ animationDelay: "75ms" }}
          >
            {messages.notFoundBody}
          </p>
        </section>
      </SiteLayout>
    );
  }

  const translationPaths = Object.fromEntries(
    locales.map((locale) => [locale, getTranslationPath(post.storyId, locale)]),
  ) as Record<Locale, string>;
  const neighbors = getPostNeighbors(site, post.slug);

  // Resolve links authored in markdown for the current locale:
  // - external links open in a new tab
  // - static assets (e.g. /files/*.pdf) keep their root path
  // - cross-post links written as /blog/<storyId> resolve to the localized slug
  // - every other internal app link gets the locale prefix
  const MarkdownLink = ({
    href: linkHref,
    children,
    node: _node,
    ...rest
  }: ComponentPropsWithoutRef<"a"> & ExtraProps) => {
    const target = linkHref ?? "";
    if (!target || isExternalHref(target) || isAssetHref(target)) {
      return (
        <a href={target} target="_blank" rel="noopener noreferrer" {...rest}>
          {children}
        </a>
      );
    }
    const storyMatch = /^\/blog\/([^/?#]+)\/?$/.exec(target);
    const resolved =
      storyMatch && getTranslations(storyMatch[1]).length > 0
        ? getTranslationPath(storyMatch[1], site.locale)
        : target;
    return (
      <a href={localizeUrl(resolved, site.locale, href)} {...rest}>
        {children}
      </a>
    );
  };

  const MarkdownParagraph = ({
    node,
    children,
    ...rest
  }: ComponentPropsWithoutRef<"p"> & ExtraProps) => {
    // Check if the paragraph consists exclusively of images (and whitespace)
    const isGallery = node?.children?.every((child) => {
      if (child.type === "element" && child.tagName === "img") return true;
      if (child.type === "text" && child.value.trim() === "") return true;
      return false;
    });

    const imageCount =
      node?.children?.filter((child) => child.type === "element" && child.tagName === "img")
        .length || 0;

    if (isGallery && imageCount === 1) {
      const image = node?.children.find(
        (child) => child.type === "element" && child.tagName === "img",
      );
      if (image?.type !== "element") return <p {...rest}>{children}</p>;
      const title = image?.properties?.title;
      if (typeof title === "string" && title.startsWith("web-app:")) {
        const appName = title.slice("web-app:".length).trim() || "Web application";
        const src = image.properties?.src;
        const alt = image.properties?.alt;
        if (typeof src !== "string") return <p {...rest}>{children}</p>;
        return (
          <MarkdownWebAppScreenshot
            src={src}
            alt={typeof alt === "string" ? alt : undefined}
            appName={appName}
          />
        );
      }
    }

    if (isGallery && imageCount > 1) {
      const images = (node?.children ?? []).flatMap((child) => {
        if (child.type !== "element" || child.tagName !== "img") return [];
        const src = child.properties?.src;
        const alt = child.properties?.alt;
        return typeof src === "string"
          ? [{ src, alt: typeof alt === "string" ? alt : undefined }]
          : [];
      });
      return <MarkdownGallery images={images} />;
    }
    return <p {...rest}>{children}</p>;
  };

  const MarkdownBlockquote = ({
    node,
    children,
    ...rest
  }: ComponentPropsWithoutRef<"blockquote"> & ExtraProps) => {
    // Look for the first paragraph element (ignoring whitespace text nodes)
    const pNode = node?.children?.find(
      (child) => child.type === "element" && child.tagName === "p",
    );
    if (pNode?.type === "element") {
      const directive = parseBlockquoteDirectiveFromParagraph(pNode);
      if (directive?.kind === "cta") return <ProductCTA id={directive.id} />;
      if (directive?.kind === "video") return <MarkdownVideo source={directive.source} />;
      if (directive?.kind === "audio")
        return <MarkdownAudio source={directive.source} title={directive.title} />;
    }
    return <blockquote {...rest}>{children}</blockquote>;
  };

  return (
    <SiteLayout languagePaths={translationPaths}>
      <BlogArticleJsonLd post={post} />
      <PageBreadcrumb items={[{ label: messages.navBlog, href: blog }, { label: post.title }]} />
      <article id="blog-article" className="relative mx-auto max-w-3xl px-6 py-16 sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 overflow-hidden"
        >
          <div className="mx-auto h-full max-w-2xl rounded-full bg-primary/8 blur-3xl" />
        </div>

        <header>
          <p
            className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium ${revealOnLoad}`}
            style={{ animationDelay: "0ms" }}
          >
            {!post.published && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
                Draft — not published
              </span>
            )}
            {post.published && isScheduledPost(post) && (
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-sky-800">
                Scheduled
              </span>
            )}
            <time className="text-muted-foreground" dateTime={post.publishedAt}>
              {formatPostDate(post.publishedAt, site.locale)}
            </time>
            {post.tags.map((tag) => (
              <span key={tag} className="flex items-center gap-2">
                <span className="text-muted-foreground/50">·</span>
                <span className="text-primary">
                  {(messages[BLOG_TAG_LABELS[tag] as keyof typeof messages] as string) || tag}
                </span>
              </span>
            ))}
          </p>
          <h1
            id="blog-article-title"
            tabIndex={-1}
            className={`mt-4 text-4xl font-semibold tracking-tight focus:outline-none sm:text-5xl ${revealOnLoad}`}
          >
            {post.title}
          </h1>
          <p
            className={`mt-4 text-lg text-muted-foreground ${revealOnLoad}`}
            style={{ animationDelay: "150ms" }}
          >
            {post.excerpt}
          </p>
        </header>
        {post.audioBrief && (
          <div className={revealOnLoad} style={{ animationDelay: "200ms" }}>
            <MarkdownAudio source={post.audioBrief} title={post.audioBriefTitle} />
          </div>
        )}
        {post.heroImage && (
          <img
            src={post.heroImage}
            alt=""
            fetchPriority="high"
            className={`mt-10 rounded-2xl border border-border ${revealOnLoad}`}
            style={{ animationDelay: "225ms" }}
          />
        )}
        <div
          id="blog-article-content"
          className={`prose prose-slate mt-10 max-w-none dark:prose-invert prose-headings:tracking-tight prose-a:text-primary ${revealOnLoad}`}
          style={{ animationDelay: post.heroImage ? "300ms" : "225ms" }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            skipHtml
            components={{ a: MarkdownLink, p: MarkdownParagraph, blockquote: MarkdownBlockquote }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
        <BlogPostNavigation
          ariaLabel={messages.navBlog}
          newer={
            neighbors.newer
              ? {
                  href: localizeUrl(`/blog/${neighbors.newer.slug}`, site.locale, href),
                  title: neighbors.newer.title,
                }
              : null
          }
          older={
            neighbors.older
              ? {
                  href: localizeUrl(`/blog/${neighbors.older.slug}`, site.locale, href),
                  title: neighbors.older.title,
                }
              : null
          }
          newerLabel={messages.blogNewerPost}
          olderLabel={messages.blogOlderPost}
        />
        <NewsletterSignup source="blog-article" variant="article" />
        <BlogLicenseNotice license={post.license} />
        <RepublishKit slug={post.slug} license={post.license} hasAudio={post.hasAudio} />
      </article>
      <BlogBackToTop
        label={messages.blogBackToTop}
        articleId="blog-article"
        contentId="blog-article-content"
        headingId="blog-article-title"
      />
    </SiteLayout>
  );
}
